import { create } from 'zustand';
import { api } from '../services/api';

export const useGamesStore = create((set, get) => ({
  // Журнал партий
  sessions: [],
  summary: { runs: 0, stars: 0, players: 0, score: 0, topPlayers: [] },
  total: 0,
  page: 1,
  totalPages: 1,
  isLoadingSessions: false,

  // Настройки игр
  games: [],
  isLoadingGames: false,

  error: null,
  success: null,

  // Номер страницы журнала. Принимает и значение, и updater-функцию,
  // как setState: кнопки пагинации считают следующую страницу от текущей,
  // а это выражение в стиле setPage((p) => p + 1), а не setPage(p + 1).
  setPage: (next) =>
    set((state) => {
      const value = typeof next === 'function' ? next(state.page) : next;
      const page = Number(value);
      return { page: Number.isFinite(page) ? Math.max(1, page) : 1 };
    }),

  // Загрузка журнала партий
  fetchSessions: async (params = {}) => {
    set({ isLoadingSessions: true, error: null });
    try {
      const data = await api.getGameSessions(params);
      set({
        sessions: data.sessions || [],
        total: data.total || 0,
        page: data.page || 1,
        totalPages: data.totalPages || 1,
        isLoadingSessions: false,
      });
      return data;
    } catch (error) {
      set({ error: error.message, isLoadingSessions: false });
      return null;
    }
  },

  // Сводка по журналу (те же фильтры, что и у таблицы)
  fetchSummary: async (params = {}) => {
    try {
      const data = await api.getGamesSummary(params);
      set({
        summary: {
          runs: data.runs || 0,
          stars: data.stars || 0,
          players: data.players || 0,
          score: data.score || 0,
          topPlayers: data.topPlayers || [],
        },
      });
      return data;
    } catch (error) {
      set({ error: error.message });
      return null;
    }
  },

  // Загрузка игр с порогами наград
  fetchGames: async () => {
    set({ isLoadingGames: true, error: null });
    try {
      const data = await api.getGames();
      set({ games: data.games || [], isLoadingGames: false });
      return data;
    } catch (error) {
      set({ error: error.message, isLoadingGames: false });
      return null;
    }
  },

  // Сохранить игру и её пороги
  saveGame: async (gameId, payload) => {
    set({ error: null, success: null });
    try {
      const data = await api.updateGame(gameId, payload);
      // Ответ содержит актуальное состояние игры — подменяем строку,
      // чтобы сервер остался единственным источником истины (id порогов).
      set((state) => ({
        games: state.games.map((game) => (game.game_id === gameId ? data.game : game)),
        success: data.message || 'Сохранено',
      }));
      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  clearError: () => set({ error: null }),
  clearSuccess: () => set({ success: null }),

  // ================= Аналитика =================
  analytics: {
    summary: null,
    series: [],
    byGame: [],
    outcomes: null,
  },
  analyticsPeriod: '30days',
  isLoadingAnalytics: false,

  setAnalyticsPeriod: (period) => set({ analyticsPeriod: period }),

  // Четыре запроса вместо одного: карточки, графики и исход партий
  // грузятся независимо, и одна медленная выборка не держит весь экран.
  fetchAnalytics: async (period) => {
    const target = period || get().analyticsPeriod;
    set({ isLoadingAnalytics: true, analyticsPeriod: target, error: null });

    const [summary, series, byGame, outcomes] = await Promise.allSettled([
      api.getGamesAnalyticsSummary(target),
      api.getGamesAnalyticsTimeseries(target),
      api.getGamesAnalyticsByGame(target),
      api.getGamesAnalyticsOutcomes(target),
    ]);

    const value = (result, fallback) => (result.status === 'fulfilled' ? result.value : fallback);
    const failures = [summary, series, byGame, outcomes].filter((r) => r.status === 'rejected');

    set((state) => ({
      analytics: {
        summary: value(summary, null) || state.analytics.summary,
        series: value(series, {}).series || [],
        byGame: value(byGame, {}).games || [],
        outcomes: value(outcomes, null) || state.analytics.outcomes,
      },
      isLoadingAnalytics: false,
      error: failures.length > 0 ? failures[0].reason?.message || 'Ошибка загрузки аналитики' : null,
    }));

    return failures.length === 0;
  },

  // Закрыть зависшие сессии. После успеха перечитываем аналитику:
  // счётчик зависших, статусы и графики на экране устарели.
  resetStaleSessions: async () => {
    set({ error: null, success: null });
    try {
      const data = await api.resetStaleGameSessions();
      await get().fetchAnalytics();
      set({ success: data.message || 'Зависшие сессии закрыты' });
      return { success: true, updated: data.updated };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },
}));
